import { map, Observable } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { SettingsService } from '../services/settings.service';
import { ServerResponse } from '../response';
import { EditMarkData } from '../models/edit-mark-data';
import { SensorData, SensorReturnData } from '../models/sensor-data';
import { TrackerPoint } from '../models/interfaces/tracker_point.interface';
import { TrackerData, TrackersData } from '../models';

@Injectable({
  providedIn: 'root',
})
export class ResultDomainService {
  constructor(
    private settingService: SettingsService,
    private http: HttpClient
  ) {}

  // Получает список трекеров и последнюю позицию участников
  getTrackers(rUid: string, clUid: string): Observable<TrackersData> {
    return this.http
      .get<ServerResponse<TrackerData[]>>(`${this.settingService.settings.urlGTM}/api/result/gps_tracker/list`, {
        params: {
          rUid,
          clUid,
        },
      })
      .pipe(
        map((res) => {
          return {
            clUid,
            trackers: res.data,
          };
        })
      );
  }

  // Получает последнюю точку GPS трекера
  getGpsTrackerLastPoint(gpsTracker: string, finGPStime: number): Observable<TrackerPoint> {
    return this.http
      .get<ServerResponse<TrackerPoint>>(`${this.settingService.settings.urlGTM}/api/result/gps_tracker/last_point`, {
        params: {
          gpsTracker,
          finGPStime,
        },
      })
      .pipe(
        map((res: any) => res.data)
      );
  }

  // Получает список отметок маршалами участника
  getCompetitorMarks(rUid: string, clUid: string, cUid: string, timeOffset: string): Observable<EditMarkData[]> {
    return this.http
      .get<EditMarkData[]>(
        `${this.settingService.settings.urlGTM}/api/result/mark/list?rUid=${rUid}&clUid=${clUid}&cUid=${cUid}`
      )
      .pipe(
        map((response: any) => {
          const rawData = response.data;
          const editData: EditMarkData[] = [];

          let lastZeroMarshal: null | EditMarkData = null;
          for (const markRaw of rawData.marks) {
            const mark = new EditMarkData({ ...markRaw, timeOffset }, 'mark');
            if (mark.marshal === 0 && !mark.delTime) {
              if (lastZeroMarshal) {
                editData.push(lastZeroMarshal);
              }
              lastZeroMarshal = mark;
            } else {
              editData.push(mark);
            }
          }

          editData.push(new EditMarkData({ markTime: rawData.start, timeOffset }, 'start'));
          if (lastZeroMarshal) {
            editData.push(
              new EditMarkData(
                {
                  markTime: lastZeroMarshal.markTime,
                  timeOffset,
                  marshal: 0,
                },
                'finish/mark'
              )
            );
          } else {
            editData.push(
              new EditMarkData(
                {
                  markTime: rawData.finish,
                  timeOffset,
                  marshal: 0,
                },
                'finish'
              )
            );
          }

          return editData;
        })
      );
  }

  // Удаляет отметку маршала
  deleteMark(rUid: string, clUid: string, cUid: string, marshal: number, markTime: number | null): Observable<void> {
    return this.http
      .delete(
        `${this.settingService.settings.urlGTM}/api/result/mark?rUid=${rUid}&clUid=${clUid}&cUid=${cUid}&marshal=${marshal}&markTime=${markTime}`
      )
      .pipe(
        map(() => undefined)
      );
  }

  // Добавляет/изменяет данные отметки маршала
  saveMark(rUid: string, clUid: string, cUid: string, marshal: number, markTime: number | null): Observable<void> {
    return this.http
      .post(`${this.settingService.settings.urlGTM}/api/result/mark`, {
        rUid,
        clUid,
        cUid,
        marshal,
        markTime,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Корректирует время старта участника
  saveStart(rUid: string, clUid: string, cUid: string, startTime: number): Observable<void> {
    return this.http
      .patch(`${this.settingService.settings.urlGTM}/api/result/start`, {
        rUid,
        clUid,
        cUid,
        startTime,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Сохраняет данные о пенализации и сходе
  saveDnfPenalty(rUid: string, clUid: string, cUid: string, penalty: number, penaltyComment: string, dnf: number, dnfComment: string): Observable<void> {
    return this.http
      .patch(`${this.settingService.settings.urlGTM}/api/result/dnf_penalty`, {
        rUid,
        clUid,
        cUid,
        penalty,
        penaltyComment,
        dnf,
        dnfComment,
      })
      .pipe(
        map(() => undefined)
      );
  }

  // Получает список таблиц очков
  getScoreTables(): Observable<any[]> {
    return this.http
      .get<ServerResponse<any>>(`${this.settingService.settings.urlGTM}/api/result/score/tables`)
      .pipe(
        map((rawData: { data: any[]; status: string }) => rawData.data)
      );
  }

  // Получает варианты подсчета результатов при равенстве очков
  getEqualScoreVariants(): Observable<any[]> {
    return this.http
      .get<ServerResponse<any>>(`${this.settingService.settings.urlGTM}/api/result/score/equal_variants?lang=${this.settingService.selectedLang}`)
      .pipe(
        map((rawData: { data: any[]; status: string }) => rawData.data)
      );
  }

  // Получает список датчиков хронометража
  getChronoTagsList(rUid: string): Observable<SensorData[]> {
    return this.http
      .get<ServerResponse<any>>(`${this.settingService.settings.urlGTM}/api/result/chrono_tag/list?rUid=${rUid}`)
      .pipe(
        map((sensors) => {
          return sensors.data.map((sensor: any) => new SensorData(sensor));
        })
      );
  }

  // Отмечает датчик хронометража как возвращенный
  markChronoTagsAsReturned(sensors: SensorReturnData[]): Observable<void> {
    return this.http
      .patch(`${this.settingService.settings.urlGTM}/api/result/chrono_tag/returned`, { sensors })
      .pipe(
        map(() => undefined)
      );
  }
}


